import { snakeCase } from 'lodash-es';

export const keys = <T extends Record<string, unknown>>(object: T) => Object.keys(object) as (keyof T)[];

export const safelyParseJSON = <TParsed = unknown>(json: string, fallbackValue: TParsed = {} as TParsed) => {
  try {
    const parsedJSON = JSON.parse(json);

    if (typeof parsedJSON !== 'object') return fallbackValue;

    return parsedJSON as TParsed;
  } catch (error) {
    return fallbackValue;
  }
};

export const isJSON = (text: string) => {
  try {
    JSON.parse(text);
  } catch (error) {
    return false;
  }

  return true;
};

export type NonNullishObject<T> = {
  [K in keyof T as Exclude<T[K], null | undefined> extends never ? never : K]: Exclude<T[K], null | undefined>;
};

/**
 * Utility type to convert a single camelCase string to snake_case
 */
type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

/**
 * Utility type to convert camelCase object keys to snake_case recursively
 */
type CamelToSnakeCaseKeys<T> = T extends readonly (infer U)[]
  ? readonly CamelToSnakeCaseKeys<U>[]
  : T extends Array<infer U>
  ? Array<CamelToSnakeCaseKeys<U>>
  : T extends Record<string, unknown>
  ? {
      [K in keyof T as CamelToSnakeCase<K & string>]: CamelToSnakeCaseKeys<T[K]>;
    }
  : T;

/**
 * Utility type for the return type of camelToSnakeCase function
 */
export type SnakeCaseObject<T> = T extends Record<string, unknown> ? CamelToSnakeCaseKeys<T> : T;

export const filterNullishKeys = <T extends Record<string, unknown>>(object: T, keysToSkip?: (keyof T)[]) => {
  const newObject = { ...object };

  keys(newObject).forEach((key) => {
    if (keysToSkip?.includes(key)) return;

    if (newObject[key] === null || newObject[key] === undefined) {
      delete newObject[key];
    }
  });

  return newObject as NonNullishObject<T>;
};

/**
 * Converts camelCase object keys to snake_case
 * @param obj The object whose keys should be converted to snake_case
 * @param keyMappings Optional mapping of specific camelCase keys to their desired snake_case equivalents
 * @returns A new object with snake_case keys
 */
export const camelToSnakeCase = <T extends Record<string, unknown>>(
  obj: T,
  keyMappings?: Record<string, string>,
): SnakeCaseObject<T> => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj as SnakeCaseObject<T>;
  }

  const result: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    // Check for hard-coded conversion first
    const snakeCaseKey = keyMappings?.[key] ?? snakeCase(key);
    const value = obj[key];

    // Recursively convert nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[snakeCaseKey] = camelToSnakeCase(value as Record<string, unknown>, keyMappings);
    } else if (Array.isArray(value)) {
      // Handle arrays - convert objects within arrays
      result[snakeCaseKey] = value.map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return camelToSnakeCase(item as Record<string, unknown>, keyMappings);
        }
        return item;
      });
    } else {
      result[snakeCaseKey] = value;
    }
  });

  return result as SnakeCaseObject<T>;
};

/**
 * Removes non-serializable fields (functions) from an object to prepare it for serialization
 * @returns A new object with all function properties removed, or the original value if not a plain object
 * @example
 * ```typescript
 * const obj = {
 *   name: 'John',
 *   age: 30,
 *   greet: () => 'Hello',
 *   calculate: function(x) { return x * 2; }
 * };
 *
 * const sanitized = stripNonSerializableFields(obj);
 * // Result: { name: 'John', age: 30 }
 * ```
 */
export const stripNonSerializableFields = <TState extends object>(
  obj: Partial<TState> | TState,
): Partial<TState> | TState => {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    const sanitizedState: Record<string, unknown> = {};
    const stateRecord = obj as Record<string, unknown>;
    for (const propertyKey in stateRecord) {
      const propertyValue = stateRecord[propertyKey];
      if (typeof propertyValue !== 'function') {
        sanitizedState[propertyKey] = propertyValue;
      }
    }
    return sanitizedState as Partial<TState>;
  }
  return obj;
};

/**
 * Performs a shallow equality check between two objects by comparing their keys and values at the first level only.
 * Returns true if both objects have the same keys and the same values for each key (using strict equality ===).
 */
export function shallowEqual<T extends object>(objA: T | null | undefined, objB: T | null | undefined): boolean {
  if (objA === objB) return true;
  if (objA == null || objB == null) return false;

  const keysA = Object.keys(objA) as (keyof T)[];
  const keysB = Object.keys(objB) as (keyof T)[];

  if (keysA.length !== keysB.length) return false;

  for (let i = 0; i < keysA.length; i++) {
    const key = keysA[i];
    if (objA[key] !== objB[key]) {
      return false;
    }
  }

  return true;
}
